import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import SignatureIcon from '@atlaskit/icon-lab/core/signature';
import { fg } from '@atlaskit/platform-feature-flags';

import SharedSalesforceDynamicMultiSelect from '../../../../../common/ui/filters/product-filters/shared-salesforce-dynamic-multi-select';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useFiltersDiscoverability2Enabled,
	useIsConfigLoaded,
} from '../../../../../controllers/store/bootstrap';
import { filter as salesforceStageFilter } from '../../../../../controllers/store/search/filters/salesforce-stage';

import { i18n } from './messages';

const SalesforceStageFilter = (props: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: salesforceStageFilter.id,
	});
	const shouldEagerLoadOptions = fg('rovo-fps-3p-ux-salesforce-dynamic-stage-filter');
	const isRetryOnConfigLoadEnabled = fg('rovo-fps-3p-ux-sf-retry-on-config-load');
	const isConfigLoaded = useIsConfigLoaded();
	// Treat as "loaded" once we have invoked loadInitialOptions with a populated bootstrap
	// config. When the retry FF is off this guard is a permanent one-shot (preserving the
	// legacy behaviour). When the FF is on, the effect below also waits for isConfigLoaded
	// to flip true before flipping this ref, so a race against the bootstrap config load
	// can no longer poison the filter into a permanent empty state.
	const hasLoadedInitialOptions = useRef(false);

	useEffect(() => {
		if (!shouldEagerLoadOptions || hasLoadedInitialOptions.current) {
			return;
		}
		// When the retry FF is enabled, hold off until bootstrap config has loaded so
		// the store can actually resolve a datasource and fetch options. Without it,
		// fall back to the legacy fire-once-on-mount behaviour.
		if (isRetryOnConfigLoadEnabled && !isConfigLoaded) {
			return;
		}
		hasLoadedInitialOptions.current = true;
		loadInitialOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldEagerLoadOptions, isRetryOnConfigLoadEnabled, isConfigLoaded]);

	if (!filterData || !shouldEagerLoadOptions) {
		return null;
	}

	const { isLoading, suggestedOptions, selectedOptions } = filterData;

	return (
		<SharedSalesforceDynamicMultiSelect
			{...props}
			filterId={salesforceStageFilter.id}
			label={formatMessage(i18n.salesforceStageFilterLabel)}
			noOptionsMessage={formatMessage(i18n.salesforceStageFilterNoOptions)}
			iconBefore={<SignatureIcon label="" />}
			isLoading={isLoading}
			selectedOptions={selectedOptions}
			suggestedOptions={suggestedOptions}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			loadInitialOptions={loadInitialOptions}
			setOption={(option) => setOption(option)}
			clearOptions={clearOptions}
		/>
	);
};

export default SalesforceStageFilter;
